<script setup>
import { computed, h } from 'vue';
import { useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';

// helpers
import { messageStamp } from 'shared/helpers/timeHelper';

// components
import Table from 'dashboard/components/table/Table.vue';
import Pagination from 'dashboard/components/table/Pagination.vue';
import UserAvatarWithName from 'dashboard/components/widgets/UserAvatarWithName.vue';
import ConversationListCell from './ConversationListCell.vue';
import Spinner from 'shared/components/Spinner.vue';
import { useReportLoading } from '../composables/useReportLoading';

import {
  useVueTable,
  createColumnHelper,
  getCoreRowModel,
} from '@tanstack/vue-table';

const props = defineProps({
  pageIndex: {
    type: Number,
    default: 0,
  },
});

const emit = defineEmits(['pageChange']);
const { t } = useI18n();

const conversations = useMapGetter('getConversationsList');
const totalConversations = useMapGetter('getConversationsListTotal');
const uiFlags = useMapGetter('getOverviewUIFlags');
const isConversationsLoading = computed(
  () => uiFlags.value?.isFetchingConversationsList
);
const visibleLoading = useReportLoading(isConversationsLoading);

const tableData = computed(() => {
  if (!conversations.value || !Array.isArray(conversations.value)) {
    return [];
  }
  return conversations.value.map(conversation => ({
    contact: conversation.contact,
    assignee: conversation.assignee,
    inbox: conversation.inbox,
    status: conversation.status,
    conversationId: conversation.id,
    createdAt: messageStamp(conversation.created_at, 'LLL d yyyy, HH:mm'),
  }));
});

const columnHelper = createColumnHelper();

const statusClasses = {
  open: 'bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-100',
  resolved: 'bg-blue-100 text-blue-800 dark:bg-blue-800 dark:text-blue-100',
  pending:
    'bg-yellow-100 text-yellow-800 dark:bg-yellow-800 dark:text-yellow-100',
  snoozed: 'bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-100',
};

const columns = [
  columnHelper.accessor('contact', {
    header: t('CONVERSATIONS_TABLE.HEADER.CONTACT_NAME'),
    width: 200,
    cell: cellProps => {
      const { contact } = cellProps.row.original;
      if (contact) {
        return h(UserAvatarWithName, {
          user: {
            name: contact.name,
            thumbnail: contact.thumbnail,
            email: contact.email,
            id: contact.id,
          },
        });
      }
      return '--';
    },
  }),
  columnHelper.accessor('assignee', {
    header: t('CONVERSATIONS_TABLE.HEADER.ASSIGNEE'),
    width: 200,
    cell: cellProps => {
      const { assignee } = cellProps.row.original;
      if (assignee) {
        return h(UserAvatarWithName, {
          user: {
            name: assignee.name,
            thumbnail: assignee.thumbnail,
            email: assignee.email,
          },
        });
      }
      return h('span', { class: 'text-slate-300 dark:text-slate-700' }, '---');
    },
  }),
  columnHelper.accessor('inbox', {
    header: t('CONVERSATIONS_TABLE.HEADER.INBOX'),
    width: 150,
    cell: cellProps => {
      const { inbox } = cellProps.row.original;
      if (inbox) {
        return h('span', inbox.name);
      }
      return '--';
    },
  }),
  columnHelper.accessor('status', {
    header: t('CONVERSATIONS_TABLE.HEADER.STATUS'),
    align: 'center',
    width: 120,
    cell: cellProps => {
      const { status } = cellProps.row.original;
      // Normalize status: trim whitespace and convert to lowercase
      const normalizedStatus = status?.toString().trim().toLowerCase();
      const statusClass = statusClasses[normalizedStatus] || statusClasses.open;

      return h(
        'span',
        {
          class: `inline-flex px-2 py-1 text-xs font-medium rounded-full ${statusClass}`,
        },
        t(`CHAT_LIST.CHAT_STATUS_FILTER_ITEMS.${normalizedStatus}.TEXT`) ||
          status
      );
    },
  }),
  columnHelper.accessor('conversationId', {
    header: t('CONVERSATIONS_TABLE.HEADER.CONVERSATION'),
    width: 100,
    cell: cellProps => h(ConversationListCell, cellProps),
  }),
  columnHelper.accessor('createdAt', {
    header: t('CONVERSATIONS_TABLE.HEADER.CREATED_AT'),
    width: 180,
  }),
];

const paginationParams = computed(() => {
  return {
    pageIndex: props.pageIndex,
    pageSize: 25,
  };
});

const table = useVueTable({
  get data() {
    return tableData.value;
  },
  columns,
  manualPagination: true,
  enableSorting: false,
  getCoreRowModel: getCoreRowModel(),
  get rowCount() {
    return totalConversations.value;
  },
  state: {
    get pagination() {
      return paginationParams.value;
    },
  },
  onPaginationChange: updater => {
    const newPagination = updater(paginationParams.value);
    emit('pageChange', newPagination.pageIndex);
  },
});
</script>

<template>
  <div class="conversations-list--table-container">
    <div
      class="relative"
      :class="{
        'min-h-[12.5rem]': visibleLoading || !tableData.length,
      }"
    >
      <Table
        :table="table"
        class="w-full max-h-[calc(100vh-21.875rem)] border bg-white dark:bg-slate-900 border-slate-50 dark:border-slate-800"
        :class="{ 'pointer-events-none opacity-50': visibleLoading }"
      />
      <div
        v-show="!tableData.length && !visibleLoading"
        class="conversations-list--empty-records text-slate-600 dark:text-slate-200 bg-white dark:bg-slate-900 border border-t-0 border-solid border-slate-75 dark:border-slate-700"
      >
        {{ $t('CONVERSATIONS_TABLE.NO_RECORDS') }}
      </div>
      <transition name="loading-fade">
        <div
          v-if="visibleLoading"
          role="status"
          aria-live="polite"
          class="absolute inset-0 z-20 flex items-center justify-center min-h-[12.5rem] border border-solid rounded-md bg-white/80 backdrop-blur-[2px] dark:bg-slate-900/80 border-slate-100 dark:border-slate-700"
        >
          <div
            class="flex flex-col items-center gap-3 px-8 py-6 bg-white border border-solid shadow-xl rounded-xl dark:bg-slate-800 border-woot-100 dark:border-slate-600"
          >
            <span
              class="flex items-center justify-center w-12 h-12 rounded-full bg-woot-50 dark:bg-woot-900/50"
            >
              <Spinner size="" color-scheme="primary" />
            </span>
            <strong
              class="text-base font-medium text-slate-800 dark:text-slate-100"
            >
              {{ $t('CONVERSATIONS_TABLE.LOADING') }}
            </strong>
          </div>
        </div>
      </transition>
    </div>
    <div v-if="totalConversations" class="table-pagination">
      <Pagination class="mt-2" :table="table" />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.conversations-list--empty-records {
  align-items: center;
  border-top: 0;
  display: flex;
  font-size: var(--font-size-small);
  height: 12.5rem;
  justify-content: center;
  margin-top: -1px;
  width: 100%;
}

.loading-fade-enter-active,
.loading-fade-leave-active {
  transition: opacity 0.15s ease;
}

.loading-fade-enter-from,
.loading-fade-leave-to {
  opacity: 0;
}
</style>
